<template>
  <div class="starred-items-container">
    <div class="section-inner">
      <!-- Tab Switch -->
      <div class="tab-bar">
        <div
          class="tab-item"
          :class="{ active: activeTab === 'deck' }"
          @click="activeTab = 'deck'"
        >Course</div>
        <div
          class="tab-item"
          :class="{ active: activeTab === 'card' }"
          @click="activeTab = 'card'"
        >Card</div>
      </div>

      <!-- ============ DECK TAB ============ -->
      <div v-if="activeTab === 'deck'" v-loading="deckLoading">
        <div class="content-grid" v-if="starredDecks.length > 0">
          <div
            v-for="deck in starredDecks"
            :key="deck.id"
            class="content-card"
            @click="openDeckRead(deck)"
          >
            <div class="card-cover">
              <CachedImage
                v-if="deck.cover_url"
                :src="deck.cover_url"
                :fallback-src="placeholderImage"
                fit="cover"
                width="100%"
                height="100%"
                lazy
              />
              <div v-else class="cover-placeholder">
                <i class="el-icon-reading"></i>
              </div>
            </div>
            <div class="card-body">
              <div class="card-body-top">
                <h4 class="card-title">{{ deck.title }}</h4>
                <p class="card-desc">{{ deck.description || 'No description' }}</p>
              </div>
              <div class="card-footer">
                <div class="card-author">
                  <el-avatar :size="20" :src="deck.user_avatar_address_small" class="author-avatar">
                    {{ deck.user_name ? deck.user_name.charAt(0) : '?' }}
                  </el-avatar>
                  <span>{{ deck.user_name || 'Unknown' }}</span>
                </div>
                <i
                  class="el-icon-star-on deck-star-icon"
                  @click.stop="confirmUnstarDeck(deck)"
                ></i>
              </div>
            </div>
          </div>
        </div>
        <div v-if="!deckLoading && starredDecks.length === 0" class="empty-state">
          <i class="el-icon-star-off"></i>
          <h3>No starred courses</h3>
          <p>Star a course from the Plaza or Course detail page to see it here</p>
        </div>
      </div>

      <!-- ============ CARD TAB ============ -->
      <div v-if="activeTab === 'card'" v-loading="cardLoading">
        <div v-if="starredCards.length > 0" class="cards-split-view">
          <!-- Left Panel: Card List -->
          <div class="cards-list-panel">
            <div
              v-for="(card, idx) in starredCards"
              :key="card.unique_code"
              class="card-list-item"
              :class="{ active: selectedCardIndex === idx }"
              @click="selectedCardIndex = idx"
            >
              <div class="card-list-cover">
                <CachedImage
                  v-if="card.collection_cover_url"
                  :src="card.collection_cover_url"
                  :fallback-src="placeholderImage"
                  fit="cover"
                  width="100%"
                  height="100%"
                />
                <div v-else class="mini-cover-placeholder">
                  <i class="el-icon-reading"></i>
                </div>
              </div>
              <div class="card-list-info">
                <span class="card-list-deck-name">{{ card.collection_title || 'Unknown Course' }}</span>
                <span class="card-list-card-title">{{ card.answer?.title || card.post_title || 'Untitled' }}</span>
              </div>
              <div class="card-list-actions">
                <i
                  class="el-icon-star-on card-star-icon"
                  @click.stop="confirmUnstarCard(card)"
                ></i>
              </div>
            </div>
          </div>

          <!-- Right Panel: Card Content -->
          <div class="card-content-panel">
            <template v-if="selectedCard">
              <div class="card-content-header">
                <h2>{{ selectedCard.answer?.title || selectedCard.post_title || 'Untitled' }}</h2>
                <span class="card-content-deck-label">{{ selectedCard.collection_title }}</span>
              </div>
              <div class="card-content-body">
                <!-- Text -->
                <div v-if="!selectedCard.answer?.content_type || selectedCard.answer?.content_type === 'text'" class="text-content">
                  <div v-html="renderContent(selectedCard.answer)"></div>
                </div>
                <!-- Image -->
                <div v-else-if="selectedCard.answer?.content_type === 'image'" class="image-content">
                  <img v-if="selectedCard.answer.fileList && selectedCard.answer.fileList.length > 0"
                    :src="selectedCard.answer.fileList[0].url" alt="Card Image" />
                </div>
                <!-- Video -->
                <div v-else-if="selectedCard.answer?.content_type === 'video'" class="video-content">
                  <video v-if="selectedCard.answer.fileList && selectedCard.answer.fileList.length > 0" controls>
                    <source :src="selectedCard.answer.fileList[0].url" :type="selectedCard.answer.fileList[0].mime_type" />
                  </video>
                </div>
                <!-- YouTube -->
                <div v-else-if="selectedCard.answer?.content_type === 'youtube'" class="youtube-content">
                  <iframe
                    :src="'https://www.youtube.com/embed/' + extractYoutubeId(selectedCard.answer.content)"
                    frameborder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowfullscreen
                  ></iframe>
                </div>
                <!-- Quiz -->
                <div v-else-if="selectedCard.answer?.content_type === 'quiz'" class="quiz-content">
                  <div class="quiz-question"><strong>Question:</strong> <span v-html="renderMarkdown(selectedCard.answer.quiz_content?.question)"></span></div>
                  <div
                    v-for="(option, idx) in (selectedCard.answer.quiz_content?.options || []).filter(o => o !== '')"
                    :key="idx"
                    class="quiz-option"
                  >
                    <div class="quiz-option-content">
                      <span style="min-width: 30px;">{{ ['A','B','C','D'][idx] }}.</span>
                      <span v-html="renderMarkdown(option)"></span>
                    </div>
                  </div>
                  <div v-if="selectedCard.answer.quiz_content?.explanation" class="quiz-explanation">
                    <div class="explanation-header">Explanation:</div>
                    <div class="explanation-content" v-html="renderMarkdown(selectedCard.answer.quiz_content.explanation)"></div>
                  </div>
                </div>
              </div>
              <div class="card-content-footer">
                <i class="el-icon-star-on card-star-icon" @click="confirmUnstarCard(selectedCard)"></i>
                <el-button size="small" type="primary" @click="goToDeck(selectedCard)">
                  Go to Course
                </el-button>
              </div>
            </template>
            <div v-else class="empty-state" style="padding: 60px 20px;">
              <i class="el-icon-document"></i>
              <p>Select a card to view its content</p>
            </div>
          </div>
        </div>
        <div v-if="!cardLoading && starredCards.length === 0" class="empty-state">
          <i class="el-icon-star-off"></i>
          <h3>No starred cards</h3>
          <p>Star a card while reading a course to see it here</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CachedImage from '@/components/CachedImage.vue'
import { postRequest } from '@/apis/front'
import { mapState } from 'vuex'
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import { renderLatex } from '@/utils/latexRenderer'

export default {
  name: 'StarredItems',
  components: { CachedImage },
  data() {
    return {
      activeTab: 'deck',
      placeholderImage: require('@/assets/Card Collection Cover Placeholder.jpeg'),
      deckLoading: false,
      cardLoading: false,
      starredDecks: [],
      starredCards: [],
      selectedCardIndex: 0
    }
  },
  computed: {
    ...mapState(['user_id']),
    selectedCard() {
      return this.starredCards[this.selectedCardIndex] || null
    }
  },
  watch: {
    activeTab(tab) {
      if (tab === 'deck' && this.starredDecks.length === 0) this.fetchStarredDecks()
      if (tab === 'card' && this.starredCards.length === 0) this.fetchStarredCards()
    }
  },
  mounted() {
    this.fetchStarredDecks()
  },
  methods: {
    async fetchStarredDecks() {
      this.deckLoading = true
      try {
        const response = await postRequest('getUserStarredCollections', {
          user_id: this.user_id
        })
        if (response.data.code === 0) {
          this.starredDecks = (response.data.data || []).map(deck => ({
            id: deck._id?.$oid || deck._id?.$id || deck.id,
            title: deck.title || 'Untitled Course',
            description: deck.description || '',
            cover_url: deck.cover_url || '',
            user_name: deck.user_name || 'Unknown',
            user_avatar_address_small: deck.user_avatar_address_small || ''
          })).reverse()
        }
      } catch (error) {
        console.error('Error fetching starred decks:', error)
      } finally {
        this.deckLoading = false
      }
    },

    async fetchStarredCards() {
      this.cardLoading = true
      try {
        const response = await postRequest('getStarredAnswerCards', {
          user_id: this.user_id
        })
        if (response.data.code === 0) {
          this.starredCards = response.data.data || []
          this.selectedCardIndex = 0
        }
      } catch (error) {
        console.error('Error fetching starred cards:', error)
      } finally {
        this.cardLoading = false
      }
    },

    confirmUnstarDeck(deck) {
      this.$confirm('Are you sure you want to unstar this course?', 'Unstar Course', {
        confirmButtonText: 'Confirm',
        cancelButtonText: 'Cancel',
        type: 'warning'
      }).then(() => {
        this.unstarDeck(deck)
      }).catch(() => {})
    },

    async unstarDeck(deck) {
      try {
        const response = await postRequest('unstarCollection', {
          user_id: this.user_id,
          collection_id: deck.id
        })
        if (response.data.code === 0) {
          this.starredDecks = this.starredDecks.filter(d => d.id !== deck.id)
          this.$message.success('Course unstarred')
        }
      } catch (error) {
        console.error('Error unstarring deck:', error)
        this.$message.error('Failed to unstar course')
      }
    },

    openDeckRead(deck) {
      this.$store.dispatch('setTempCollectionId', deck.id)
      this.$router.push({ path: '/read-collection', query: { source: 'starred' } })
    },

    goToDeck(card) {
      if (!card.collection_id) return
      this.$store.dispatch('setTempCollectionId', card.collection_id)
      this.$router.push({
        path: '/read-collection',
        query: { source: 'starred', post_id: card.post_id, unique_code: card.unique_code }
      })
    },

    confirmUnstarCard(card) {
      this.$confirm('Are you sure you want to unstar this card?', 'Unstar Card', {
        confirmButtonText: 'Confirm',
        cancelButtonText: 'Cancel',
        type: 'warning'
      }).then(() => {
        this.unstarCard(card)
      }).catch(() => {})
    },

    async unstarCard(card) {
      try {
        const response = await postRequest('toggleStarAnswer', {
          user_id: this.user_id,
          post_id: card.post_id,
          uniqueCode: card.unique_code,
          is_starred: false
        })
        if (response.data.code === 0) {
          this.starredCards = this.starredCards.filter(c => c.unique_code !== card.unique_code)
          if (this.selectedCardIndex >= this.starredCards.length) {
            this.selectedCardIndex = Math.max(0, this.starredCards.length - 1)
          }
          this.$message.success('Card unstarred')
        }
      } catch (error) {
        console.error('Error unstarring card:', error)
        this.$message.error('Failed to unstar card')
      }
    },

    renderContent(answer) {
      if (!answer || !answer.content) return ''
      const katexTags = ['span', 'math', 'semantics', 'mrow', 'mi', 'mo', 'mn', 'msup', 'msub', 'mfrac', 'munder', 'mover', 'msqrt', 'mroot', 'mtable', 'mtr', 'mtd', 'annotation', 'svg', 'line', 'path', 'rect', 'g']
      const katexAttrs = ['class', 'style', 'aria-hidden', 'xmlns', 'width', 'height', 'viewBox', 'preserveAspectRatio', 'd', 'fill', 'stroke', 'x', 'y', 'x1', 'x2', 'y1', 'y2', 'encoding']
      const editorType = answer.editor_type || 'auto'

      if (editorType === 'markdown' || this.detectMarkdownPatterns(answer.content)) {
        marked.setOptions({ breaks: true, gfm: true, headerIds: false, mangle: false })
        let rawHtml = marked(answer.content)
        rawHtml = renderLatex(rawHtml)
        return DOMPurify.sanitize(rawHtml, { ADD_TAGS: ['mark', ...katexTags], ADD_ATTR: ['target', 'rel', ...katexAttrs] })
      }
      if (editorType === 'richtext' || this.detectHtmlTags(answer.content)) {
        let result = renderLatex(answer.content)
        return DOMPurify.sanitize(result, { ADD_TAGS: katexTags, ADD_ATTR: katexAttrs })
      }
      let escaped = answer.content.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br>')
      escaped = renderLatex(escaped)
      return DOMPurify.sanitize(escaped, { ALLOWED_TAGS: ['br', ...katexTags], ADD_ATTR: katexAttrs })
    },

    renderMarkdown(text) {
      if (!text) return ''
      marked.setOptions({ breaks: true, gfm: true, headerIds: false, mangle: false })
      let html = marked(text)
      html = renderLatex(html)
      return DOMPurify.sanitize(html)
    },

    detectMarkdownPatterns(content) {
      if (!content) return false
      const patterns = [/^#{1,6}\s/m, /\*\*.+?\*\*/, /\[.+?\]\(.+?\)/, /^[-*+]\s/m, /^\d+\.\s/m, /^>\s/m, /```/]
      return patterns.some(p => p.test(content))
    },

    detectHtmlTags(content) {
      if (!content) return false
      return /<\/?[a-z][\s\S]*>/i.test(content)
    },

    extractYoutubeId(url) {
      if (!url) return ''
      const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/)
      return match ? match[1] : ''
    }
  }
}
</script>

<style scoped>
.starred-items-container {
  width: 100%;
  min-height: 100vh;
  background: #f7f8fc;
}

.section-inner {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

/* ── Tab Bar ── */
.tab-bar {
  display: flex;
  gap: 0;
  padding: 24px 0 20px;
}

.tab-item {
  padding: 8px 28px;
  font-size: 15px;
  font-weight: 600;
  color: #909399;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: all 0.2s;
}

.tab-item:hover {
  color: #606266;
}

.tab-item.active {
  color: #409eff;
  border-bottom-color: #409eff;
}

/* ── Deck Grid (matches Plaza) ── */
.content-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  padding-bottom: 60px;
}

.content-card {
  background: #fff;
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
}

.content-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
}

.card-cover {
  width: 100%;
  aspect-ratio: 3 / 2;
  background: #eef1f6;
  overflow: hidden;
}

.cover-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #e8ecf1 0%, #d5dce6 100%);
}

.cover-placeholder i {
  font-size: 32px;
  color: #b0bac5;
}

.card-body {
  padding: 12px 14px 14px;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.card-body-top {
  flex: 1;
}

.card-title {
  margin: 0 0 4px;
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.35;
}

.card-desc {
  margin: 0 0 10px;
  font-size: 12px;
  color: #909399;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.4;
}

.card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.card-author {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #606266;
  min-width: 0;
}

.author-avatar {
  background: #e0e0e0;
  color: #606266;
  font-size: 10px;
  flex-shrink: 0;
}

.card-author span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.deck-star-icon {
  font-size: 18px;
  color: #f7ba2a;
  cursor: pointer;
  transition: color 0.2s, transform 0.15s;
  flex-shrink: 0;
}

.deck-star-icon:hover {
  color: #e6a23c;
  transform: scale(1.15);
}

/* ── Card Split View ── */
.cards-split-view {
  display: flex;
  gap: 0;
  height: calc(100vh - 140px);
  background: #fff;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.cards-list-panel {
  width: 420px;
  min-width: 420px;
  border-right: 1px solid #ebeef5;
  overflow-y: auto;
}

.card-list-item {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  gap: 12px;
  cursor: pointer;
  border-bottom: 1px solid #f2f3f5;
  transition: background 0.15s;
}

.card-list-item:hover {
  background: #f5f7fa;
}

.card-list-item.active {
  background: #ecf5ff;
  border-left: 3px solid #409eff;
  padding-left: 13px;
}

.card-list-cover {
  width: 56px;
  height: 56px;
  border-radius: 6px;
  overflow: hidden;
  flex-shrink: 0;
  background: #eef1f6;
}

.mini-cover-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #e8ecf1 0%, #d5dce6 100%);
}

.mini-cover-placeholder i {
  font-size: 18px;
  color: #b0bac5;
}

.card-list-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.card-list-deck-name {
  font-size: 12px;
  color: #909399;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.card-list-card-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.card-list-actions {
  flex-shrink: 0;
}

.card-star-icon {
  font-size: 18px;
  color: #f7ba2a;
  cursor: pointer;
  transition: color 0.2s;
}

.card-star-icon:hover {
  color: #e6a23c;
}

/* ── Card Content Panel ── */
.card-content-panel {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  min-width: 0;
}

.card-content-header {
  padding: 24px 32px 16px;
  border-bottom: 1px solid #f2f3f5;
}

.card-content-header h2 {
  margin: 0 0 6px;
  font-size: 20px;
  font-weight: 700;
  color: #1a1a2e;
}

.card-content-deck-label {
  font-size: 13px;
  color: #909399;
}

.card-content-body {
  flex: 1;
  padding: 24px 32px;
  overflow-y: auto;
  line-height: 1.8;
  font-size: 15px;
  color: #303133;
}

.card-content-body .text-content {
  word-break: break-word;
}

.card-content-body .image-content img {
  max-width: 100%;
  border-radius: 8px;
}

.card-content-body .video-content video {
  max-width: 100%;
  border-radius: 8px;
}

.card-content-body .youtube-content iframe {
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: 8px;
}

.card-content-body .quiz-question {
  margin-bottom: 16px;
  font-size: 15px;
}

.card-content-body .quiz-option {
  padding: 10px 14px;
  margin-bottom: 8px;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  background: #fafbfc;
}

.card-content-body .quiz-option-content {
  display: flex;
  align-items: flex-start;
  gap: 4px;
}

.card-content-body .quiz-explanation {
  margin-top: 16px;
  padding: 14px;
  background: #f0f9eb;
  border-radius: 8px;
}

.card-content-body .explanation-header {
  font-weight: 600;
  margin-bottom: 6px;
  color: #67c23a;
}

.card-content-footer {
  padding: 12px 32px;
  border-top: 1px solid #f2f3f5;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

/* ── Empty States ── */
.empty-state {
  text-align: center;
  padding: 80px 20px;
  color: #909399;
}

.empty-state i {
  font-size: 56px;
  color: #dcdfe6;
}

.empty-state h3 {
  margin: 16px 0 8px;
  color: #606266;
  font-weight: 600;
}

.empty-state p {
  margin: 0;
  font-size: 14px;
}

/* ── Responsive ── */
@media (max-width: 1100px) {
  .content-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 768px) {
  .content-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .cards-split-view {
    flex-direction: column;
    height: auto;
  }
  .cards-list-panel {
    width: 100%;
    min-width: 0;
    max-height: 300px;
    border-right: none;
    border-bottom: 1px solid #ebeef5;
  }
  .section-inner {
    padding: 0 16px;
  }
}

@media (max-width: 480px) {
  .content-grid {
    grid-template-columns: 1fr;
  }
}
</style>
